<script setup lang="ts">
import { usePreferredReducedMotion } from '@vueuse/core';
import { computed } from 'vue';

import { AppSpinner } from '@/shared/ui/spinner';

import type { PullGesturePhase } from '../lib';

import { resolvePullIndicatorPresentation } from '../model/use-pull-to-refresh';

const props = defineProps<{
  phase: PullGesturePhase;
  pullDistance: number;
  isRefreshing: boolean;
}>();

const preferredMotion = usePreferredReducedMotion();
const presentation = computed(() =>
  resolvePullIndicatorPresentation({
    phase: props.phase,
    isRefreshing: props.isRefreshing,
    prefersReducedMotion: preferredMotion.value === 'reduce',
  }),
);
const indicatorOffsetPx = computed(() => (props.isRefreshing ? 24 : Math.max(0, props.pullDistance - 12)));
const indicatorStyle = computed(() => ({
  opacity: presentation.value.isVisible ? '1' : '0',
  transform: `translate3d(-50%, ${indicatorOffsetPx.value}px, 0)`,
}));
</script>

<template>
  <div
    role="status"
    aria-live="polite"
    aria-atomic="true"
    :aria-hidden="!presentation.isVisible"
    class="pointer-events-none fixed top-[max(0.5rem,env(safe-area-inset-top))] left-1/2 z-[1060] flex h-10 w-10 items-center justify-center rounded-full bg-(--base-container-bg) text-(--base-accent-color) shadow-lg"
    :class="presentation.shouldAnimatePosition ? 'transition-[opacity,transform] duration-150 ease-out' : ''"
    :style="indicatorStyle"
  >
    <AppSpinner
      v-if="isRefreshing"
      aria-hidden="true"
      size="sm"
    />
    <svg
      v-else
      aria-hidden="true"
      class="h-5 w-5 transition-transform duration-150 motion-reduce:transition-none"
      :class="phase === 'armed' ? 'rotate-180' : ''"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M12 3v14" />
      <path d="m6 11 6 6 6-6" />
    </svg>
    <span class="sr-only">{{ presentation.label }}</span>
  </div>
</template>
